/**
 * Markup layer: named ".{filename}.marks", stored in localStorage for browser access.
 * Records colored text highlights with creation timestamps and save timestamps.
 */

export type MarkEntry = {
  /** Unique identifier (e.g. "m1abc4z"). */
  id: string;
  /** The text content at the time of marking. */
  text: string;
  /** Start char offset (inclusive) in the document. */
  start: number;
  /** End char offset (exclusive) in the document. */
  end: number;
  /** CSS color string (e.g. "rgba(255,220,0,0.55)"). */
  color: string;
  /** ISO 8601 timestamp when this mark was created. */
  startDate: string;
  /** ISO 8601 timestamp when the document was next saved; null = current unsaved session. */
  endDate: string | null;
};

export type MarkupFile = { version: 1; marks: MarkEntry[] };

export type MarkColorPreset = { label: string; value: string };

export const MARK_COLOR_PRESETS: readonly MarkColorPreset[] = [
  { label: "Yellow", value: "rgba(255,220,0,0.55)" },
  { label: "Green", value: "rgba(60,200,100,0.45)" },
  { label: "Blue", value: "rgba(70,145,255,0.45)" },
  { label: "Pink", value: "rgba(255,80,150,0.40)" },
  { label: "Orange", value: "rgba(255,140,40,0.50)" },
] as const;

/** Dot-file name for a given document filename: "notes.md" → ".notes.md.marks" */
export function marksFilenameFor(filename: string): string {
  return `.${filename}.marks`;
}

/** localStorage key for a given file path or tab id. */
export function markupStorageKey(filePath: string): string {
  return `npp-markup:${filePath}`;
}

export function parseMarkupFile(json: string): MarkupFile | null {
  try {
    const p = JSON.parse(json) as unknown;
    if (
      p !== null &&
      typeof p === "object" &&
      (p as Record<string, unknown>).version === 1 &&
      Array.isArray((p as Record<string, unknown>).marks)
    ) {
      return p as MarkupFile;
    }
  } catch {
    // ignore parse errors
  }
  return null;
}

export function serializeMarkupFile(marks: MarkEntry[]): string {
  const file: MarkupFile = { version: 1, marks };
  return JSON.stringify(file, null, 2);
}

// ── Share bundle ────────────────────────────────────────────────────────────

/**
 * Null-byte sentinels appended to the share payload when marks exist.
 * Null characters never appear in normal prose text, so old clients that
 * don't know about this format will only see trailing whitespace/garbage
 * at the very end of the document — harmless in practice.
 */
const SHARE_MARKS_SEP = "\x00\x00NPP_MARKS_V1\x00\x00";
const SHARE_MARKS_END = "\x00\x00NPP_MARKS_END\x00\x00";

/**
 * Append serialized marks to the tab text so both travel as one share payload.
 * Returns `text` unchanged when `marks` is empty.
 */
export function bundleSharePayload(text: string, marks: MarkEntry[]): string {
  if (marks.length === 0) return text;
  return `${text}${SHARE_MARKS_SEP}${serializeMarkupFile(marks)}${SHARE_MARKS_END}`;
}

/**
 * Split a received share payload back into document text and optional marks.
 * If no bundle sentinel is found, treats the whole payload as plain text.
 */
export function unbundleSharePayload(payload: string): { text: string; marks: MarkEntry[] | null } {
  const sepIdx = payload.indexOf(SHARE_MARKS_SEP);
  if (sepIdx < 0) return { text: payload, marks: null };
  const text = payload.slice(0, sepIdx);
  const rest = payload.slice(sepIdx + SHARE_MARKS_SEP.length);
  const endIdx = rest.indexOf(SHARE_MARKS_END);
  if (endIdx < 0) return { text: payload, marks: null }; // malformed — fall back to raw
  const parsed = parseMarkupFile(rest.slice(0, endIdx));
  return { text, marks: parsed?.marks ?? null };
}

/** Generate a short unique ID for a mark entry. */
export function newMarkId(): string {
  return `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

/** Load marks from localStorage. Returns [] if not found or invalid. */
export function loadMarkupFromStorage(storageKey: string): MarkEntry[] {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    return parseMarkupFile(raw)?.marks ?? [];
  } catch {
    return [];
  }
}

/** Persist marks to localStorage (removes key when marks array is empty). */
export function persistMarkupToStorage(storageKey: string, marks: MarkEntry[]): void {
  try {
    if (marks.length === 0) {
      localStorage.removeItem(storageKey);
    } else {
      localStorage.setItem(storageKey, serializeMarkupFile(marks));
    }
  } catch {
    // quota exceeded or private-browsing restriction
  }
}

function escapeHtml(str: string): string {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function escapeAttr(val: string): string {
  return val.replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

/**
 * Build the inner HTML for the markup overlay `<code>` element.
 *
 * Structure mirrors `wrapHighlightLines`: each newline-separated line is wrapped
 * in `<div class="hl-line">`. Marked regions within a line get a
 * `<span class="npp-mark-hl">` with an inline background-color. Non-marked text
 * has no colour applied; `color:transparent` on the parent `<pre>` makes it
 * invisible so only the highlight backgrounds are visible.
 */
export function renderMarkupOverlayHtml(text: string, marks: MarkEntry[]): string {
  const lines = text.split("\n");
  const parts: string[] = new Array(lines.length);
  let lineStart = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    const lineEnd = lineStart + line.length;

    const lineMarks = marks
      .filter((m) => m.end > lineStart && m.start < lineEnd)
      .sort((a, b) => a.start - b.start);

    let lineHtml = "";
    let pos = lineStart;

    for (const mark of lineMarks) {
      const ms = Math.max(lineStart, mark.start);
      const me = Math.min(lineEnd, mark.end);
      if (pos < ms) lineHtml += escapeHtml(text.slice(pos, ms));
      if (ms < me) {
        lineHtml +=
          `<span class="npp-mark-hl" style="background-color:${escapeAttr(mark.color)}" ` +
          `data-mark-id="${escapeAttr(mark.id)}">${escapeHtml(text.slice(ms, me))}</span>`;
        pos = me;
      }
    }

    if (pos <= lineEnd) lineHtml += escapeHtml(text.slice(pos, lineEnd));

    // Zero-width space keeps the div non-empty so line-height is preserved.
    parts[i] = `<div class="hl-line">${lineHtml.length === 0 ? "\u200b" : lineHtml}</div>`;
    lineStart = lineEnd + 1; // +1 for the '\n'
  }

  return parts.join("");
}
